import catalog from "@/assets/ciblesV3";

// Normalisation centralisée des clés d'images (nettoyage query string, trim, etc.)
function cleanCatalogKey(key: string): string {
    // Retire tout éventuel suffixe de requête ?v=, ?cacheBust, etc.
    const noQuery = key.split("?")[0];
    // Trim espaces accidentels
    return noQuery.trim();
}

// Types plus précis basés sur l'analyse du catalogue
export type ImageType = "geometrie" | "objet";
export type GeometrieSpecificite = "plein" | "vide" | "horizontal" |"diagonal1" | "diagonal2" | "vertical";
export type ObjetsSpecificite = "fixe" | "mouvement";
export type ColorImages = "bleu" | "vert" | "rouge" | "jaune" | "orange" | "violet" | "rose" | "noir" | "blanc" | "gris" | "nb" | "couleur";
export type GeometrieFormes = "trait" | "etoile" | "carre" | "losange" | "play" | "rond" | "soleil" | "triangle";
export type ObjetsCategories = string; // Trop nombreux pour les énumérer tous
export type DescriptionImages = GeometrieFormes | ObjetsCategories | string;

// Interface plus précise pour les paramètres de sélection
export interface ImageSelectionParams {
    // Paramètres obligatoires
    type: ImageType[];
    
    // Paramètres spécifiques selon le type
    geometrieSpecificite?: GeometrieSpecificite[];
    objetsSpecificite?: ObjetsSpecificite[];
    
    // Paramètres de couleur
    couleurInclude?: ColorImages[];
    couleurExclude?: ColorImages[];
    
    // Paramètres de forme/description
    geometrieFormes?: GeometrieFormes[];
    objetsCategories?: ObjetsCategories[];
    
    // Paramètres avancés
    excludePatterns?: string[]; // Patterns à exclure des noms
    includePatterns?: string[]; // Patterns qui doivent être présents
    maxResults?: number; // Limite le nombre de résultats
}

export const selectImagesCatalog = (params: ImageSelectionParams): { [key: string]: string } => {
    const {
        type,
        geometrieSpecificite,
        objetsSpecificite,
        couleurInclude,
        couleurExclude,
        geometrieFormes,
        objetsCategories,
        excludePatterns,
        includePatterns,
        maxResults
    } = params;
    const filteredImages = Object.entries(catalog).filter(([rawKey]: [string, string]) => {
        const key = cleanCatalogKey(rawKey);
        const parts = key.split('_');
        if (parts.length < 3) return false; // Minimum structure required
        
    const [cat, spec, color, ...rest] = parts;
        const name = rest.join('_');
        // console.log("Processing key:", key, "Category:", cat, "Specificity:", spec, "Color:", color, "Name:", name);
        // Check type
        // console.log(`Checkingg type: ${cat}`,type, !type.includes(cat as ImageType));
        if (!type.includes(cat as ImageType)) return false;
        // console.log('etape 2')
        // Check spécificité selon le type
        // console.log(`Checking specificity: ${spec}`);
        // console.log(cat === "geometrie" && objetsSpecificite && objetsSpecificite.length > 0);
        if (cat === "geometrie" && geometrieSpecificite && geometrieSpecificite.length > 0) {
            // console.log(`Geometrie specificite: ${geometrieSpecificite.includes(spec as GeometrieSpecificite)}`);
            if (!geometrieSpecificite.includes(spec as GeometrieSpecificite)) return false;
        }
        // console.log(spec,`Checking specificity: ${spec}`);
        if (cat === "objet" && objetsSpecificite && objetsSpecificite.length > 0) {
            // console.log(cat,`Objets specificite: ${objetsSpecificite.includes(spec as ObjetsSpecificite)}`);
            if (!objetsSpecificite.includes(spec as ObjetsSpecificite)) return false;
        }

        // Check couleurInclude
        // console.log(`Checking color: ${color}, include: ${couleurInclude}, exclude: ${couleurExclude}`);
        if (couleurInclude && couleurInclude.length > 0) {
            // console.log(`Couleur include: ${couleurInclude.includes(color as ColorImages)}`);
            if (!couleurInclude.includes(color as ColorImages)) return false;
        }

        // Check couleurExclude
        // console.log(`Checking color: ${color}, include: ${couleurInclude}, exclude: ${couleurExclude}`);
        if (couleurExclude && couleurExclude.length > 0) {
            // console.log(`Couleur exclude: ${couleurExclude.includes(color as ColorImages)}`);
            if (couleurExclude.includes(color as ColorImages)) return false;
        }

        // Check formes géométriques
        if (cat === "geometrie" && geometrieFormes && geometrieFormes.length > 0) {
            //console.log(`Geometrie formes: ${!geometrieFormes.some(forme => name.includes(forme))}`);
            if (!geometrieFormes.some(forme => name.includes(forme))) return false;
        }

        // Check catégories d'objets
        if (cat === "objet" && objetsCategories && objetsCategories.length > 0) {
            //console.log(`Objets categories: ${!objetsCategories.some(categorie => name.includes(categorie))}`);
            if (!objetsCategories.some(categorie => name.includes(categorie))) return false;
        }

        // Check exclude patterns
       // console.log(`Checking exclude patterns: ${excludePatterns}`);
        if (excludePatterns && excludePatterns.length > 0) {
            // console.log(`Exclude patterns: ${excludePatterns.some(pattern => key.includes(pattern))}`);
            if (excludePatterns.some(pattern => key.includes(pattern))) return false;
        }

        // Check include patterns
        // console.log(`Checking include patterns: ${includePatterns}`);
        if (includePatterns && includePatterns.length > 0) {
            // console.log(`Include patterns: ${includePatterns.some(pattern => key.includes(pattern))}`);
            if (!includePatterns.some(pattern => key.includes(pattern))) return false;
        }

        return true;
    });
    
    // Nettoie aussi les valeurs (chemins) pour retirer d'éventuelles query strings
    let result = Object.fromEntries(
        filteredImages.map(([rawKey, rawValue]) => [
            cleanCatalogKey(rawKey),
            (typeof rawValue === 'string' ? rawValue.split('?')[0].trim() : rawValue) as string,
        ])
    );
    
    // Limiter les résultats si demandé
    if (maxResults && Object.keys(result).length > maxResults) {
        const entries = Object.entries(result);
        const limitedEntries = entries.slice(0, maxResults);
        result = Object.fromEntries(limitedEntries);
    }
    console.log(`Selected ${Object.keys(result).length} images with params:`, result, params);
    return result;
};

// Fonction de compatibilité pour l'ancienne interface
export const selectImagesCatalogLegacy = (
    type: ImageType[], 
    specificite: (GeometrieSpecificite | ObjetsSpecificite)[], 
    couleurInclude: ColorImages[], 
    couleurExclude: ColorImages[], 
    description: DescriptionImages[]
): { [key: string]: string } => {
    return selectImagesCatalog({
        type,
        geometrieSpecificite: specificite.filter(s => ['plein', 'vide', 'horizontal', 'rond'].includes(s)) as GeometrieSpecificite[],
        objetsSpecificite: specificite.filter(s => ['fixe', 'mouvement'].includes(s)) as ObjetsSpecificite[],
        couleurInclude,
        couleurExclude,
        geometrieFormes: description.filter(d => ['trait', 'etoile', 'carre', 'losange', 'play', 'rond', 'soleil', 'triangle'].includes(d)) as GeometrieFormes[],
        objetsCategories: description.filter(d => !['trait', 'etoile', 'carre', 'losange', 'play', 'rond', 'soleil', 'triangle'].includes(d)) as ObjetsCategories[]
    });
};